import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { PhotoCamera, Delete as DeleteIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateApplication, useEditApplication, useApplication } from '../../hooks/useApplications';
import { usePermissions } from '../../hooks/useAuth';
import AddressSelector from '../Shared/AddressSelector';
import LoadingOverlay from '../Shared/LoadingOverlay';
import { useAddressData } from '../../hooks/useAddressData';

// Validation schema based on the API requirements
const validationSchema = Yup.object({
  applicant_name: Yup.string().required('Applicant name is required'),
  sodowo: Yup.string().required('Son/Daughter/Wife of is required'),
  village: Yup.string().required('Village is required'),
  ward_id: Yup.string().required('Ward is required'),
  land_khatian_number: Yup.string().required('Land Khatian number is required'),
  land_plot_nos: Yup.string().required('Land plot numbers are required'),
  ph_number: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
    .required('Phone number is required'),
  voter_id: Yup.string().required('Voter ID is required'),
  damage_type: Yup.array()
    .min(1, 'Please select at least one damage type')
    .required('Damage type is required'),
  actual_nc_datetime: Yup.date()
    .required('Natural calamity date and time is required'),
});

const DAMAGE_TYPES = ['crop', 'livestock', 'land', 'house'];
const RELATION_OPTIONS = ['S/o', 'W/o', 'D/o'];

const ExgratiaApplicationForm = () => {
  const navigate = useNavigate();
  const { refno } = useParams(); // For editing existing applications
  const { hasPermission } = usePermissions();
  
  const [isEditing, setIsEditing] = useState(false);

  const [relationPrefix, setRelationPrefix] = useState(RELATION_OPTIONS[0]);
  const [relationName, setRelationName] = useState('');

  // Address selection hook
  const {
    selectedDistrict,
    selectedSubdivision,
    selectedBlock,
    selectedGPU,
    selectedWard,
    handleDistrictChange,
    handleSubdivisionChange,
    handleBlockChange,
    handleGPUChange,
    handleWardChange,
    getCompleteAddress,
    setSelections,
    isLoading: isLoadingAddress,
  } = useAddressData();

  // Image state
  const [images, setImages] = useState([]);
  const [imageErrors, setImageErrors] = useState('');

  // API hooks
  const createMutation = useCreateApplication();
  const editMutation = useEditApplication();
  
  // For editing - fetch existing application
  const { data: existingApplication, isLoading: isLoadingApplication } = useApplication(
    'refno', 
    refno, 
    { enabled: !!refno }
  );

  const formik = useFormik({
    initialValues: {
      applicant_name: '',
      sodowo: '',
      village: '',
      ward_id: '',
      land_khatian_number: '',
      land_plot_nos: '',
      ph_number: '',
      voter_id: '',
      damage_type: [],
      actual_nc_datetime: '',
      remarks: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const submitData = {
          ...values,
          ward_id: selectedWard, 
          damage_type: values.damage_type,
        };

        if (isEditing) {
          await editMutation.mutateAsync({
            applicationRefno: refno,
            applicationData: submitData
          });
          navigate(`/applications/${refno}`);
        } else {
          const response = await createMutation.mutateAsync({
            applicationData: submitData,
            images: images
          });
          navigate(`/applications/${response.application.application_refno || response.application.id}`);
        }
      } catch (error) {
        console.error('Submission error:', error);
      }
    },
  });

  useEffect(() => {
    if (selectedWard) {
      formik.setFieldValue('ward_id', selectedWard);
    }
  }, [selectedWard]);

  useEffect(() => {
    const combined = `${relationPrefix} ${relationName}`.trim();
    formik.setFieldValue('sodowo', combined);
  }, [relationPrefix, relationName]);

  // Handle existing application data for editing
  useEffect(() => {
    if (refno && existingApplication) {
      setIsEditing(true);
      // Pre-populate form with existing data
      Object.keys(existingApplication).forEach(key => {
        if (formik.values.hasOwnProperty(key)) {
          formik.setFieldValue(key, existingApplication[key]);
        }
      });
      
      if (existingApplication.ward) {
        const ward = existingApplication.ward;
        if (ward.gpu?.block?.subdivision?.district) {
          setSelections({
            district: ward.gpu.block.subdivision.district.id,
            subdivision: ward.gpu.block.subdivision.id,
            block: ward.gpu.block.id,
            gpu: ward.gpu.id,
            ward: ward.id,
          });
        }
      }

      if (existingApplication.sodowo) {
        const sodowoValue = existingApplication.sodowo.trim();
        const match = sodowoValue.match(/^(S\/o|W\/o|D\/o)\s+(.*)$/i);
        if (match) {
          const prefix = match[1];
          const name = match[2];
          if (RELATION_OPTIONS.includes(prefix)) setRelationPrefix(prefix);
          setRelationName(name);
        } else {
          setRelationName(sodowoValue);
        }
      }
    }
  }, [existingApplication, refno]);

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    setImageErrors('');

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name} is not an image file`);
        return;
      }

      // Check file size (max 3MB)
      if (file.size > 3 * 1024 * 1024) {
        errors.push(`${file.name} is too large (max 3MB)`);
        return;
      }

      // Check total images limit (max 3)
      if (images.length + validFiles.length >= 3) {
        errors.push('Maximum 3 images allowed');
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setImageErrors(errors.join(', '));
      return;
    }

    setImages(prev => [...prev, ...validFiles]);
    event.target.value = '';
  };

  const handleImageRemove = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const formatDateTimeForInput = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toISOString().slice(0, 16);
  };

  // Permission check
  if (!hasPermission('apply_exgratia')) {
    return (
      <Alert severity="error">
        You don't have permission to create or edit applications.
      </Alert>
    );
  }

  if (isLoadingApplication) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const formatDateTimeForAPI = (datetime) => {
    if (!datetime) return '';
    // Convert datetime-local format to YYYY-MM-DD HH:MM:SS
    return datetime.replace('T', ' ');
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {isEditing ? 'Edit Ex-Gratia Application' : 'Apply for Ex-Gratia'}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" mb={3}>
        {isEditing 
          ? `Edit application: ${refno}`
          : 'Submit your application for natural calamity ex-gratia compensation'
        }
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Personal Information
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 3 }}>
                    <TextField
                      fullWidth
                      label="Applicant Name"
                      name="applicant_name"
                      value={formik.values.applicant_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.applicant_name && Boolean(formik.errors.applicant_name)}
                      helperText={formik.touched.applicant_name && formik.errors.applicant_name}
                      required
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <TextField
                        label="Son/Daughter/Wife of"
                        value={relationName}
                        onChange={(e) => setRelationName(e.target.value)}
                        onBlur={() => formik.setFieldTouched('sodowo', true)}
                        error={formik.touched.sodowo && Boolean(formik.errors.sodowo)}
                        helperText={formik.touched.sodowo && formik.errors.sodowo}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Select
                                value={relationPrefix}
                                onChange={(e) => setRelationPrefix(e.target.value)}
                                onBlur={() => formik.setFieldTouched('sodowo', true)}
                                variant="standard"
                                sx={{ mr: 1 }}
                              >
                                {RELATION_OPTIONS.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </Select>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="ph_number"
                      value={formik.values.ph_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.ph_number && Boolean(formik.errors.ph_number)}
                      helperText={formik.touched.ph_number && formik.errors.ph_number}
                      required
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Voter ID"
                      name="voter_id"
                      value={formik.values.voter_id}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.voter_id && Boolean(formik.errors.voter_id)}
                      helperText={formik.touched.voter_id && formik.errors.voter_id}
                      required
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Information */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Address Information
                </Typography>
                
                <Grid container spacing={2}>                  
                  <Grid size={{ xs: 12 }}>
                    <AddressSelector
                      selectedDistrict={selectedDistrict}
                      selectedSubdivision={selectedSubdivision}
                      selectedBlock={selectedBlock}
                      selectedGPU={selectedGPU}
                      selectedWard={selectedWard}
                      onDistrictChange={handleDistrictChange}
                      onSubdivisionChange={handleSubdivisionChange}
                      onBlockChange={handleBlockChange}
                      onGPUChange={handleGPUChange}
                      onWardChange={handleWardChange}
                      required
                      size="medium"
                    />
                    {formik.touched.ward_id && formik.errors.ward_id && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {formik.errors.ward_id}
                      </Typography>
                    )}
                    {getCompleteAddress() && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">Selected Address:</Typography>
                        {getCompleteAddress()}
                      </Alert>
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Village"
                      name="village"
                      value={formik.values.village}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.village && Boolean(formik.errors.village)}
                      helperText={formik.touched.village && formik.errors.village}
                      required
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Land Information */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Land Information
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Land Khatian Number"
                      name="land_khatian_number"
                      value={formik.values.land_khatian_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.land_khatian_number && Boolean(formik.errors.land_khatian_number)}
                      helperText={formik.touched.land_khatian_number && formik.errors.land_khatian_number}
                      required
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Land Plot Numbers"
                      name="land_plot_nos"
                      value={formik.values.land_plot_nos}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.land_plot_nos && Boolean(formik.errors.land_plot_nos)}
                      helperText={formik.touched.land_plot_nos && formik.errors.land_plot_nos}
                      required
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Damage Information */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Damage Information
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl 
                      fullWidth 
                      error={formik.touched.damage_type && Boolean(formik.errors.damage_type)}
                    >
                      <InputLabel>Damage Type</InputLabel>
                      <Select
                        name="damage_type"
                        multiple
                        value={formik.values.damage_type}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Damage Type"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                        required
                      >
                        {DAMAGE_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.damage_type && formik.errors.damage_type && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                          {formik.errors.damage_type}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Natural Calamity Date & Time"
                      name="actual_nc_datetime"
                      type="datetime-local"
                      value={formatDateTimeForInput(formik.values.actual_nc_datetime)}
                      onChange={(e) => {
                        formik.setFieldValue('actual_nc_datetime', formatDateTimeForAPI(e.target.value));
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.actual_nc_datetime && Boolean(formik.errors.actual_nc_datetime)}
                      helperText={formik.touched.actual_nc_datetime && formik.errors.actual_nc_datetime}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Remarks"
                      name="remarks"
                      value={formik.values.remarks}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                      helperText={formik.touched.remarks && formik.errors.remarks}
                      multiline
                      rows={3}
                      placeholder="Additional information about the damage..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Supporting Images Section */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Supporting Images
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload up to 3 images showing damage evidence (max 5MB per image)
                </Typography>
                
                <Box sx={{ mt: 2, mb: 2 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    type="file"
                    multiple
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCamera />}
                      disabled={images.length >= 3}
                      sx={{ mr: 2 }}
                    >
                      {images.length === 0 ? 'Upload Images' : `Add More Images (${images.length}/3)`}
                    </Button>
                  </label>
                </Box>

                {imageErrors && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {imageErrors}
                  </Alert>
                )}

                {images.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Images:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {images.map((image, index) => (
                        <Chip
                          key={index}
                          label={image.name}
                          onDelete={() => handleImageRemove(index)}
                          deleteIcon={<DeleteIcon />}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Submit Button */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate('/applications')}
                disabled={createMutation.isLoading || editMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  createMutation.isLoading || 
                  editMutation.isLoading || 
                  isLoadingAddress || 
                  !selectedWard ||
                  !formik.isValid
                }
                startIcon={
                  (createMutation.isLoading || editMutation.isLoading) && 
                  <CircularProgress size={20} />
                }
              >
                {isEditing ? 'Update Application' : 'Submit Application'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Loading Overlay */}
      <LoadingOverlay
        open={createMutation.isLoading || editMutation.isLoading}
        message={
          createMutation.isLoading 
            ? 'Submitting application...' 
            : editMutation.isLoading 
              ? 'Updating application...'
              : 'Processing...'
        }
      />
    </Box>
  );
};

export default ExgratiaApplicationForm;
